import React, { useState } from "react";
import { Dropdown, Menu } from "antd";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import API, { getReferralsUrl, getUserProfile } from "api";
import { trackEvent } from "api/analytics";
import { copyToClipboard } from "Utils";
import { ReactComponent as ReferralCopied } from "assets/Icon/referralCopiedIcon.svg";
import { ReactComponent as AccountIcon } from "assets/IconsV2/Account.svg";
import { ReactComponent as SupportIcon } from "assets/IconsV2/Support.svg";
import InAppHelpIcon from "components/support/InAppHelpIcon";
import ReferModal from "./ReferModal";
import styles from "./Header.module.css";

/**
 * The account control that used to live only in the Header: help, the avatar, and its menu.
 *
 * Ask's compact shell hides the Header, so the same menu has to live on the app nav overlay or
 * a reader on a phone has no way to log out.
 */
export default function AccountMenu({ isPublic, className, showHelp = true }) {
    const [showReferModal, setShowReferModal] = useState(false);
    const [visibleMenu, setVisibleMenu] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const history = useHistory();
    const user = useSelector(({ layout }) => layout.user);
    const userProfile = getUserProfile();

    const menu = (
        <Menu
            className="header-menu pb-0"
            onClick={() => {
                setVisibleMenu(true);
            }}
        >
            {isPublic ? (
                <Menu.Item className="user-section flex flex-col">
                    <label className="mb-2 block">
                        <b>Jones Ferdinand</b>
                    </label>
                </Menu.Item>
            ) : user && userProfile ? (
                <Menu.Item className="user-section flex flex-col">
                    <label className="mb-2 block">
                        <b>
                            {user.first_name} {user.last_name}
                        </b>
                    </label>
                    <label className="mb-2 block">
                        <i>{userProfile.company_name}</i>
                    </label>
                    <label>{user.email}</label>
                </Menu.Item>
            ) : null}

            <Menu.Item
                key="account"
                className={styles.menuItem}
                onClick={() => {
                    history.push("/user/account/");
                    trackEvent("Account Mgmt - Click within Dropdown");
                }}
            >
                Favorites & Notifications
            </Menu.Item>
            <Menu.Item
                key="privacy"
                className={styles.menuItem}
                onClick={() => {
                    history.push("/privacy");
                    trackEvent("Privacy & Terms");
                }}
            >
                Privacy & Terms
            </Menu.Item>
            <Menu.Item
                key="1"
                className={styles.menuItem}
                onClick={({ domEvent }) => {
                    domEvent.stopPropagation();
                    domEvent.preventDefault();
                    copyToClipboard(getReferralsUrl());
                    setLinkCopied(true);
                    trackEvent("Account Mgmt - Copy Referral Link");
                }}
            >
                {linkCopied ? (
                    <>
                        Referral link copied{" "}
                        <ReferralCopied style={{ display: "inline-block", verticalAlign: "bottom" }} />
                    </>
                ) : (
                    "Copy Referral Link"
                )}
                <span> ({userProfile?.referrals_count || 0} sent)</span>
            </Menu.Item>
            <Menu.Item
                key="3"
                className={styles.menuItem}
                onClick={() => {
                    trackEvent("Account Mgmt - User Logout");
                    API.logout();
                }}
            >
                Log out
            </Menu.Item>
        </Menu>
    );

    const supportItem = (
        <button type="button" className="sider-account-item">
            <SupportIcon />
            <span>Support</span>
        </button>
    );

    if (!user && !isPublic) return showHelp ? <InAppHelpIcon>{supportItem}</InAppHelpIcon> : null;

    return (
        <div className={clsx("account-menu", className)}>
            <ReferModal visible={showReferModal} onCancel={() => setShowReferModal(false)} />
            {showHelp ? <InAppHelpIcon>{supportItem}</InAppHelpIcon> : null}
            {user || isPublic ? (
                <Dropdown
                    overlay={menu}
                    onOpenChange={(visible) => {
                        setVisibleMenu(visible);
                        setTimeout(() => {
                            if (!visible) setLinkCopied(false);
                        }, 1000);
                    }}
                    open={visibleMenu}
                >
                    <button type="button" className="sider-account-item">
                        <AccountIcon />
                        <span>Account</span>
                    </button>
                </Dropdown>
            ) : null}
        </div>
    );
}
